import { useState, useEffect } from "react";
import "@/App.css";
import { Phone, MapPin, Clock, Menu, X, ChevronDown, Star, Bed, Utensils, Music, Beer, Mail, Calendar } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL (White transparent version)
const LOGO_URL = "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/3bpyd5rt_BLACK-TRANSP.png";

// Image URLs
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/d5t4c7gb_5G7A8772.jpg",
  accommodation: "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/ny134rql_room.jpg",
  dining: "https://images.unsplash.com/photo-1724452146582-617589cddc99?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwcHViJTIwZm9vZCUyMGJ1cmdlciUyMHN0ZWFrJTIwc2FsYWR8ZW58MHx8fHwxNzcwNDIxMTc0fDA&ixlib=rb-4.1.0&q=85",
  entertainment: "https://customer-assets.emergentagent.com/job_64e85de0-743f-4481-bbc9-0922edf9ce9c/artifacts/anpp3pu2_605707384_1469501298517031_8551778185563398926_n.jpg",
  pub: "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/tqlhonbg_1.jpg",
  about: "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/m28a0zir_2.jpg",
  gallery: [
    "https://images.unsplash.com/photo-1559110624-e44eebc9a833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBwdWIlMjBpbnRlcmlvciUyMHZpYnJhbnQlMjBhdG1vc3BoZXJlJTIwcGVvcGxlJTIwZHJpbmtpbmd8ZW58MHx8fHwxNzcwNDIxMTcwfDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1619683551667-4bc326abff44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxnb3VybWV0JTIwcHViJTIwZm9vZCUyMGJ1cmdlciUyMHN0ZWFrJTIwc2FsYWR8ZW58MHx8fHwxNzcwNDIxMTc0fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1590675560125-0d832b9d719e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMHJvb20lMjBpbnRlcmlvciUyMGNvbWZvcnRhYmxlJTIwYmVkJTIwYnJpZ2h0fGVufDB8fHx8MTc3MDQyMTE3OXww&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1699545009030-ed949bff61ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsaXZlJTIwbXVzaWMlMjBiYW5kJTIwcGVyZm9ybWluZyUyMGluJTIwYmFyJTIwdmlicmFudCUyMGxpZ2h0c3xlbnwwfHx8fDE3NzA0MjExODR8MA&ixlib=rb-4.1.0&q=85"
  ]
};

// Navigation Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [setScrolled]);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Stay", href: "#accommodation" },
    { name: "Pub", href: "#pub" },
    { name: "Menu", href: "#menu" },
    { name: "Events", href: "#events" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="Tocumwal Hotel Motel" className="h-28 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className="text-amber-100 hover:text-amber-400 transition-colors font-medium text-sm tracking-wide"
              >
                {link.name}
              </a>
            ))}
          </div>

          <div className="hidden lg:block">
            <a href="https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); window.open('https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html', '_blank'); }}>
              <Button
                data-testid="book-now-btn"
                className="bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full px-6 py-2 transition-all hover:scale-105 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
              >
                Book Now
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden text-amber-100"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div data-testid="mobile-menu" className="lg:hidden glass rounded-lg mt-2 p-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                className="block py-3 text-amber-100 hover:text-amber-400 transition-colors font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a href="https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); window.open('https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html', '_blank'); setIsOpen(false); }}>
              <Button className="w-full mt-4 bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full">
                Book Now
              </Button>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Tocumwal Hotel Motel Atmosphere"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A0F0A]/80 via-[#1A0F0A]/60 to-[#1A0F0A]"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="animate-fade-in-up">
          <img
            src={LOGO_URL}
            alt="Tocumwal Hotel Motel"
            className="w-64 sm:w-80 lg:w-[420px] mx-auto mb-8"
          />
        </div>
        
        <h1
          data-testid="hero-title"
          className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold text-amber-50 mb-6 tracking-tight animate-fade-in-up animation-delay-100"
        >
          Heritage <span className="text-amber-400 text-glow">Reborn</span>
        </h1>
        
        <p
          data-testid="hero-subtitle"
          className="text-lg sm:text-xl text-amber-200 mb-8 max-w-2xl mx-auto animate-fade-in-up animation-delay-200"
        >
          Experience the charm of 1861 with the comfort of 2025. Stay, dine, and unwind at Tocumwal&apos;s iconic destination.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-300">
          <a href="#accommodation">
            <Button
              data-testid="hero-book-btn"
              className="bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full px-8 py-3 text-lg transition-all hover:scale-105 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
            >
              <Bed className="mr-2 h-5 w-5" /> Book a Room
            </Button>
          </a>
          <a href="#menu">
            <Button
              data-testid="hero-menu-btn"
              className="bg-transparent border-2 border-amber-500 text-amber-400 hover:bg-amber-500/10 rounded-full px-8 py-3 text-lg"
            >
              <Utensils className="mr-2 h-5 w-5" /> View Menu
            </Button>
          </a>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="text-amber-400 h-8 w-8" />
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => {
  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">Our Story</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4 mb-6">
              A Legacy of <span className="text-amber-400">Hospitality</span>
            </h2>
            <p className="text-amber-200 text-lg leading-relaxed mb-6">
              Since 1861, the Tocumwal Hotel Motel has been the heart of our community. Nestled on the banks of the Murray River, we&apos;ve welcomed travelers, locals, and adventurers for over 160 years.
            </p>
            <p className="text-amber-300 leading-relaxed mb-8">
              Now reborn in 2025, we blend our rich heritage with modern comfort. From our cozy rooms to our vibrant pub, every corner tells a story of Australian hospitality at its finest.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="glass rounded-lg p-6 text-center">
                <span className="font-serif text-4xl text-amber-400 font-bold">160+</span>
                <p className="text-amber-200 mt-2">Years of History</p>
              </div>
              <div className="glass rounded-lg p-6 text-center">
                <span className="font-serif text-4xl text-amber-500 font-bold">2025</span>
                <p className="text-amber-200 mt-2">Reborn & Renewed</p>
              </div>
            </div>
          </div>
          
          <div className="relative">
            <div className="glass rounded-lg p-2">
              <div className="relative">
                <img
                  src={IMAGES.about}
                  alt="Hotel Interior"
                  className="w-full h-80 lg:h-[500px] object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 glass rounded-lg p-4 flex items-center gap-3">
              <div className="bg-amber-500 rounded-full p-2">
                <Star className="h-6 w-6 text-amber-950" />
              </div>
              <div>
                <p className="text-amber-50 font-bold">Highly Rated</p>
                <p className="text-amber-300 text-sm">Loved by our guests</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Accommodation Section
const Accommodation = () => {
  const rooms = [
    {
      name: "Standard Room",
      price: "$120/night",
      features: ["Queen Bed", "Ensuite Bathroom", "Air Conditioning", "FREE WiFi", "LED TV", "Fridge", "Coffee & Toast Making Facilities"],
    },
    {
      name: "Deluxe Room",
      price: "$140/night",
      features: ["Queen Bed", "Ensuite Bathroom", "Air Conditioning", "FREE WiFi", "LED TV", "Fridge", "Coffee & Toast Making Facilities"],
    },
    {
      name: "Family Room",
      price: "$180/night",
      features: ["Queen Bed & Single Bed", "Ensuite Bathroom / Kitchenette", "Air Conditioning", "FREE WiFi", "LED TV", "Fridge", "Coffee & Toast Making Facilities"],
    },
  ];

  return (
    <section id="accommodation" data-testid="accommodation-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-[#150b07]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">Accommodation</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4">
            Rest & <span className="text-amber-400">Recharge</span>
          </h2>
          <p className="text-amber-300 mt-4 max-w-2xl mx-auto">
            Comfortable rooms with modern amenities, just steps from the Murray River.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {rooms.map((room, index) => (
            <div
              key={room.name}
              data-testid={`room-card-${index}`}
              className="glass rounded-lg overflow-hidden group hover:border-amber-500/50 transition-all duration-300"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={IMAGES.accommodation}
                  alt={room.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/40"></div>
                <div className="absolute top-4 right-4 bg-amber-500 text-amber-950 font-bold px-3 py-1 rounded-full text-sm z-10">
                  {room.price}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-2xl text-amber-50 mb-4">{room.name}</h3>
                <ul className="space-y-2">
                  {room.features.map((feature) => (
                    <li key={feature} className="text-amber-300 flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <a href="https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html" target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); window.open('https://www.booking.com/hotel/au/tocumwal-motel-tocumwal4.html', '_blank'); }}>
                  <Button className="w-full mt-6 bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full">
                    Book This Room
                  </Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pub Section
const Pub = () => {
  return (
    <section id="pub" data-testid="pub-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="glass rounded-lg p-2">
              <div className="relative">
                <img
                  src={IMAGES.pub}
                  alt="Pub Atmosphere"
                  className="w-full h-80 lg:h-[500px] object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
              </div>
            </div>
            <div className="absolute -top-6 -right-6 glass rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Beer className="h-8 w-8 text-amber-400" />
                <div>
                  <p className="text-amber-50 font-bold">Cold Beers</p>
                  <p className="text-amber-300 text-sm">On Tap</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">The Pub</span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4 mb-6">
              Drink & <span className="text-amber-400">Connect</span>
            </h2>
            <p className="text-amber-200 text-lg leading-relaxed mb-6">
              Our pub is the social heart of Tocumwal. With a great selection of local and international beers, premium spirits, and our signature cocktails, there&apos;s something for everyone.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="bg-amber-900/60 rounded-full p-3">
                  <Beer className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-50 font-semibold">Wide Selection</p>
                  <p className="text-amber-300 text-sm">Local craft beers & imported favorites</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-amber-900/60 rounded-full p-3">
                  <Clock className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-50 font-semibold">Happy Hour</p>
                  <p className="text-amber-300 text-sm">Monday to Friday, 4pm - 6pm</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-amber-900/60 rounded-full p-3">
                  <Music className="h-6 w-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-amber-50 font-semibold">Live Entertainment</p>
                  <p className="text-amber-300 text-sm">Music & Events</p>
                </div>
              </div>
            </div>

            <div className="glass rounded-lg p-4 flex items-center gap-4">
              <Clock className="h-6 w-6 text-amber-400" />
              <div>
                <p className="text-amber-50 font-bold">Opening Hours</p>
                <p className="text-amber-300">11am till late, 7 days a week</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Menu Section
const FoodMenu = () => {
  const menuCategories = [
    {
      name: "Starters",
      items: [
        { name: "Garlic Bread", price: "$10", desc: "Garlic and herb butter on toasted Turkish bread" },
        { name: "Loaded Wedges", price: "$14", desc: "Bacon, cheese, sour cream & sweet chilli" },
        { name: "Salt & Pepper Calamari (GF)", price: "$18", desc: "Squid lightly dusted with lime and pepper seasoning, served with tartare sauce and a lemon wedge" },
      ],
    },
    {
      name: "Mains",
      items: [
        { name: "Classic Beef Burger", price: "$22", desc: "Home made beef patty, cheese, lettuce, tomato, onion, beetroot and burger sauce on a soft potato bun" },
        { name: "Chicken Parmi", price: "$28", desc: "Ham, napoli sauce, mozzarella, chips & salad" },
        { name: "Memphis Style Rack O Ribs (GF DF)", price: "$36", desc: "Signature candy red whisky glazed bbq pork ribs served with chips and slaw" },
        { name: "350g Grass Fed Scotch Fillet", price: "$42", desc: "Served with your choice of chips and salad or mash potato and veg" },
      ],
    },
    {
      name: "Kids Menu",
      items: [
        { name: "Fish & Chips", price: "$12", desc: "With tomato sauce" },
        { name: "Chicken Nuggets", price: "$12", desc: "With chips & sauce" },
        { name: "Mini Burger", price: "$12", desc: "Beef patty, cheese, chips" },
      ],
    },
  ];

  return (
    <section id="menu" data-testid="menu-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-[#150b07]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">Our Menu</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4">
            Eat & <span className="text-amber-400">Enjoy</span>
          </h2>
          <p className="text-amber-300 mt-4 max-w-2xl mx-auto">
            Fresh, hearty pub classics made with locally sourced ingredients.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Food Images Column */}
          <div className="space-y-4">
            {/* Main image */}
            <div className="glass rounded-lg p-2">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/djv151si_1.jpg"
                  alt="Pub Food Selection"
                  className="w-full h-64 lg:h-72 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
              </div>
            </div>
            {/* Two images side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass rounded-lg p-2">
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/ww8t1ihd_2.jpg"
                    alt="Burger with Fries"
                    className="w-full h-40 lg:h-48 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
                </div>
              </div>
              <div className="glass rounded-lg p-2">
                <div className="relative">
                  <img
                    src="https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/suebmftv_4.jpg"
                    alt="Corn Ribs"
                    className="w-full h-40 lg:h-48 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
                </div>
              </div>
            </div>
            {/* Ribs image */}
            <div className="glass rounded-lg p-2">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/m96w6vrx_5.jpg"
                  alt="BBQ Ribs with Coleslaw"
                  className="w-full h-64 lg:h-72 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
              </div>
            </div>
            {/* View Full Menu Button */}
            <div className="text-center mt-6">
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/menu-pdf`}
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="view-full-menu-btn"
              >
                <Button className="bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full px-8 py-3 transition-all hover:scale-105 shadow-[0_0_20px_rgba(245,158,11,0.3)]">
                  Click here to view our Full Menu
                </Button>
              </a>
            </div>
          </div>
          
          <div className="space-y-8">
            {menuCategories.map((category) => (
              <div key={category.name} data-testid={`menu-category-${category.name.toLowerCase()}`}>
                <h3 className="font-serif text-2xl text-amber-400 mb-4 pb-2 border-b border-amber-800">
                  {category.name}
                </h3>
                <div className="space-y-4">
                  {category.items.map((item) => (
                    <div key={item.name} className="flex justify-between items-start">
                      <div>
                        <p className="text-amber-50 font-semibold">{item.name}</p>
                        <p className="text-amber-400 text-sm">{item.desc}</p>
                      </div>
                      <span className="text-amber-500 font-bold">{item.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Food & Entertainment Section
const Events = () => {
  const events = [
    {
      day: "Tuesday",
      title: "ALL DAY STEAK DAY — $26",
      descBold: "250g Grass Fed Scotch Fillet",
      desc: "Cooked to your liking with your choice of sides.",
      time: "During set meal times",
    },
    {
      day: "Wednesday",
      title: "ALL DAY PARMA DAY — $22",
      descBold: "4 Amazing Parma choices",
      desc: "Served with your choice of sides.",
      time: "During set meal times",
    },
    {
      day: "Sat / Sun",
      title: "ENTERTAINMENT",
      descBold: "",
      desc: "With so much talent in and around the region over\nsome weekends we love to showcase these artists.\n\nKeep an eye out on our socials to find out more.",
      time: "",
    },
    {
      day: "Sunday",
      title: "ALL DAY ROAST — $22",
      descBold: "Alternating each week between Pork & Lamb",
      desc: "Served with your choice of sides.",
      time: "During set meal times",
    },
  ];

  return (
    <section id="events" data-testid="events-section" className="relative py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="absolute inset-0 z-0">
        <img
          src={IMAGES.entertainment}
          alt="Food & Entertainment at Tocumwal Hotel"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">What&apos;s On</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4 mb-6">
            Food & <span className="text-amber-400">Entertainment</span>
          </h2>
          <p className="text-amber-200 text-lg leading-relaxed max-w-3xl mx-auto">
            There&apos;s always something happening at the Tocumwal Hotel. From live music to amazing meal deals and even trivia nights, join us for great times and good memories.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {events.map((event) => (
            <div
              key={event.title}
              data-testid={`event-${event.day.toLowerCase().replace(/\s*\/\s*/g, '-')}`}
              className="glass rounded-lg p-6 hover:border-amber-500/50 transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-amber-500 text-amber-950 font-bold px-3 py-2 rounded-lg text-center text-sm min-w-[80px]">
                  {event.day}
                </div>
                <h3 className="text-amber-50 font-bold text-base">{event.title}</h3>
              </div>
              {event.descBold && (
                <p className="text-amber-50 font-bold text-sm mt-1">{event.descBold}</p>
              )}
              <p className="text-amber-200 text-sm leading-relaxed whitespace-pre-line">{event.desc}</p>
              {event.time && (
                <p className="text-amber-400 text-xs mt-3 font-semibold uppercase tracking-wide">
                  <Clock className="inline h-3 w-3 mr-1" />{event.time}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Social Links */}
        <div className="flex items-center justify-center gap-6 mt-10" data-testid="events-social-links">
          <a
            href="https://www.facebook.com/Tocumwalhotelmotel"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); window.open('https://www.facebook.com/Tocumwalhotelmotel', '_blank'); }}
            data-testid="social-facebook"
            className="glass rounded-full p-4 hover:border-amber-500/50 transition-all group"
          >
            <svg className="h-6 w-6 text-amber-400 group-hover:text-amber-300 transition-colors" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
          <a
            href="https://www.instagram.com/tocumwalhotelmotel/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => { e.preventDefault(); window.open('https://www.instagram.com/tocumwalhotelmotel/', '_blank'); }}
            data-testid="social-instagram"
            className="glass rounded-full p-4 hover:border-amber-500/50 transition-all group"
          >
            <svg className="h-6 w-6 text-amber-400 group-hover:text-amber-300 transition-colors" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </a>
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = {
    hero: "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/5um44sbq_1leftside.jpg",
    grid: [
      "https://customer-assets.emergentagent.com/job_64e85de0-743f-4481-bbc9-0922edf9ce9c/artifacts/0zxz4jjk_TOC-HOTEL.jpg",
      "https://customer-assets.emergentagent.com/job_64e85de0-743f-4481-bbc9-0922edf9ce9c/artifacts/tyekemjn_DECK.jpg",
      "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/dno7cfjb_1.jpg",
      "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/2sf2isuv_2.jpg",
      "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/z30hz9r9_3.jpg",
      "https://customer-assets.emergentagent.com/job_vibrant-hotel/artifacts/ds94myby_4.jpg",
    ]
  };

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-[#150b07]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">Gallery</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4">
            See Our <span className="text-amber-400">Space</span>
          </h2>
          <p className="text-amber-300 mt-4 max-w-2xl mx-auto">
            Take a virtual tour of our hotel, pub, and dining areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {/* Left side - Hero image */}
          <div 
            className="glass rounded-lg p-1 cursor-pointer group"
            onClick={() => setSelectedImage(galleryImages.hero)}
          >
            <div className="relative h-full min-h-[400px] lg:min-h-[620px]">
              <img
                src={galleryImages.hero}
                alt="Gallery Hero"
                className="w-full h-full object-cover rounded-lg group-hover:opacity-80 transition-opacity"
              />
              <div className="absolute inset-0 bg-black/20 rounded-lg group-hover:bg-black/10 transition-all"></div>
            </div>
          </div>
          
          {/* Right side - Grid of images */}
          <div className="grid grid-cols-2 gap-4">
            {galleryImages.grid.map((img, index) => (
              <div
                key={img}
                data-testid={`gallery-image-${index}`}
                className="cursor-pointer group overflow-hidden rounded-lg"
                onClick={() => setSelectedImage(img)}
              >
                <div className="relative">
                  <img
                    src={img}
                    alt={`Gallery ${index + 1}`}
                    className="w-full h-full object-cover rounded-lg group-hover:opacity-80 transition-opacity aspect-[4/3]"
                  />
                  <div className="absolute inset-0 bg-black/20 rounded-lg group-hover:bg-black/10 transition-all"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {selectedImage && (
          <div
            data-testid="gallery-lightbox"
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage}
              alt="Gallery preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        )}
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    guests: "",
    date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/bookings`, formData);
      toast.success("Booking request sent! We'll contact you shortly.");
      setFormData({ name: "", email: "", phone: "", guests: "", date: "", message: "" });
    } catch (error) {
      toast.error("Failed to send. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-amber-500 font-semibold tracking-wider text-sm uppercase">Contact Us</span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-amber-50 mt-4">
            Get In <span className="text-amber-400">Touch</span>
          </h2>
          <p className="text-amber-300 mt-4 max-w-2xl mx-auto">
            Book your stay, reserve a table, or just say hello.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="space-y-6 mb-8">
              <div className="glass rounded-lg p-6 flex items-center gap-4">
                <div className="bg-amber-500 rounded-full p-3">
                  <MapPin className="h-6 w-6 text-amber-950" />
                </div>
                <div>
                  <p className="text-amber-50 font-bold">Address</p>
                  <p className="text-amber-300">17-32 Deniliquin Street, Tocumwal, NSW, 2714</p>
                </div>
              </div>
              
              <div className="glass rounded-lg p-6 flex items-center gap-4">
                <div className="bg-amber-400 rounded-full p-3">
                  <Phone className="h-6 w-6 text-amber-950" />
                </div>
                <div>
                  <p className="text-amber-50 font-bold">Mobile</p>
                  <a href="tel:0348036122" className="text-amber-300 hover:text-amber-400 transition-colors">
                    (03) 4803 6122
                  </a>
                </div>
              </div>
              
              <div className="glass rounded-lg p-6 flex items-center gap-4">
                <div className="bg-amber-500 rounded-full p-3">
                  <Clock className="h-6 w-6 text-amber-950" />
                </div>
                <div>
                  <p className="text-amber-50 font-bold">Opening Hours</p>
                  <p className="text-amber-300">11am till late, 7 days a week</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="glass rounded-lg p-2 overflow-hidden">
              <iframe
                data-testid="google-map"
                title="The Palms Tocumwal Hotel Motel Location"
                src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=The+Palms+Tocumwal+Hotel+Motel,Tocumwal+NSW+Australia"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>

          {/* Booking Form */}
          <div className="glass rounded-lg p-8">
            <h3 className="font-serif text-2xl text-amber-50 mb-6">Book Your Stay</h3>
            <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-amber-200 text-sm mb-2 block">Name *</label>
                  <Input
                    data-testid="input-name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-amber-200 text-sm mb-2 block">Email *</label>
                  <Input
                    data-testid="input-email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-amber-200 text-sm mb-2 block">Mobile <span className="text-amber-500">*</span></label>
                  <Input
                    data-testid="input-phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500"
                    placeholder="Your mobile number"
                  />
                </div>
                <div>
                  <label className="text-amber-200 text-sm mb-2 block">Number of Guests</label>
                  <Input
                    data-testid="input-guests"
                    type="number"
                    min="1"
                    value={formData.guests}
                    onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                    className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500"
                    placeholder="1"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-amber-200 text-sm mb-2 block">Preferred Date</label>
                <Input
                  data-testid="input-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500"
                />
              </div>
              
              <div>
                <label className="text-amber-200 text-sm mb-2 block">Message</label>
                <Textarea
                  data-testid="input-message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-amber-950/50 border-amber-800 text-amber-50 focus:border-amber-500 min-h-[120px]"
                  placeholder="Any special requests or questions..."
                />
              </div>
              
              <p className="text-amber-400 text-sm font-bold italic whitespace-pre-line" data-testid="booking-disclaimer">
                *PLEASE NOTE: This is a Booking Request ONLY{"\n"}We will contact you to confirm your booking.
              </p>

              <Button
                data-testid="submit-booking-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 text-amber-950 hover:bg-amber-400 font-bold rounded-full py-3 transition-all hover:scale-105 shadow-[0_0_20px_rgba(245,158,11,0.3)]"
              >
                {loading ? "Sending..." : "Send Booking Request"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-[#0d0705] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          <div>
            <img src={LOGO_URL} alt="Tocumwal Hotel Motel" className="h-16 mb-4" />
            <p className="text-amber-300 text-sm">
              Est. 1861 • Reborn 2025<br />
              Your home away from home in Tocumwal.
            </p>
          </div>
          
          <div>
            <h4 className="text-amber-50 font-bold mb-4">Quick Links</h4>
            <div className="space-y-2">
              <a href="#about" className="block text-amber-300 hover:text-amber-400 transition-colors">About Us</a>
              <a href="#accommodation" className="block text-amber-300 hover:text-amber-400 transition-colors">Accommodation</a>
              <a href="#menu" className="block text-amber-300 hover:text-amber-400 transition-colors">Menu</a>
              <a href="#events" className="block text-amber-300 hover:text-amber-400 transition-colors">Events</a>
            </div>
          </div>
          
          <div>
            <h4 className="text-amber-50 font-bold mb-4">Contact</h4>
            <div className="space-y-2 text-amber-300 text-sm">
              <p className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-amber-500" />
                17-32 Deniliquin Street, Tocumwal, NSW, 2714
              </p>
              <p className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-amber-500" />
                <a href="tel:0348036122" className="hover:text-amber-400 transition-colors">(03) 4803 6122</a>
              </p>
              <p className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                11am till late, 7 days
              </p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-amber-900 pt-8 text-center">
          <p className="text-amber-500 text-sm">
            © {new Date().getFullYear()} Tocumwal Hotel Motel. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App bg-[#1A0F0A] min-h-screen">
      <Toaster position="top-right" richColors />
      <Navbar />
      <Hero />
      <About />
      <Accommodation />
      <Pub />
      <FoodMenu />
      <Events />
      <Gallery />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
